import axios from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import MockAdapter from 'axios-mock-adapter';
import Cookies from 'cookies-js';
import config from '../../config';

const buildId = process.env.BUILD_ID || 'default';
const headers = {
    'X-Client-Name': `buildId - ${buildId}`,
};

const addJwtInterceptor = (instance) => {
    instance.interceptors.request.use(
        (config) => {
            const jwtToken = Cookies.get('jwt');
            if (jwtToken) {
                config.headers.Authorization = `Bearer ${jwtToken}`;
            }
            return config;
        },
        (error) => Promise.reject(error),
    );
};

// Reroute v2/search requests to the dedicated search API host.
// v3/v4 search (leagues) uses getLeaguesSearchApiAxiosInstance() with its own baseURL.
// In proxy mode the server-side middleware handles this, so the interceptor is a no-op.
const v2SearchPathRegex = /^\/?v2\/search(\/|$|\?)/;
const addSearchRouteInterceptor = (instance) => {
    if (config.alwaysUseProxy) {
        return;
    }
    instance.interceptors.request.use(
        (reqConfig) => {
            if (v2SearchPathRegex.test(reqConfig.url)) {
                reqConfig.baseURL = config.searchApiHost;
            }
            return reqConfig;
        },
        (error) => Promise.reject(error),
    );
};

export const getProxyAxiosInstance = (jwt = null) => {
    const instance = axios.create({
        baseURL: '/api',
        headers,
    });

    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }

    return instance;
};

export const getEngageProxyAxiosInstance = (jwt = null) => {
    const instance = axios.create({
        baseURL: '/engage-api',
        headers,
    });

    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }
    addJwtInterceptor(instance);
    return instance;
};

export const getApiAxiosInstance = (jwt = null) => {
    if (config.alwaysUseProxy) {
        return getProxyAxiosInstance(jwt);
    }

    const instance = axios.create({
        baseURL: config.apiHost,
        withCredentials: true,
        headers,
    });

    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }

    addSearchRouteInterceptor(instance);
    addJwtInterceptor(instance);

    return instance;
};

export const getEngageApiAxiosInstance = (jwt = Cookies.get('jwt')) => {
    if (config.alwaysUseProxy) {
        return getEngageProxyAxiosInstance(jwt);
    }

    const instance = axios.create({
        baseURL: config.engageApiHost,
        withCredentials: true,
        headers,
    });

    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }
    addJwtInterceptor(instance);
    return instance;
};

export const getAxiosInstance = () => {
    return axios.create({});
};

export const getLeaguesProxyAxiosInstance = () => {
    return axios.create({
        baseURL: '/leagues-api',
    });
};

export const getGroupsProxyAxiosInstance = () => {
    return axios.create({
        baseURL: '/groups-api',
    });
};

export const getRostersProxyAxiosInstance = () => {
    return axios.create({
        baseURL: '/rosters-api',
        headers,
    });
};

export const getRankingsProxyAxiosInstance = () => {
    const instance = axios.create({
        baseURL: '/rankings-api',
    });
    addJwtInterceptor(instance);
    return instance;
};

export const getLeaguesApiAxiosInstance = () => {
    if (config.alwaysUseProxy) {
        return getLeaguesProxyAxiosInstance();
    }
    return axios.create({
        baseURL: config.leaguesApiHost,
        withCredentials: true,
    });
};

export const getGroupsApiAxiosInstance = () => {
    if (config.alwaysUseProxy) {
        return getGroupsProxyAxiosInstance();
    }
    return axios.create({
        baseURL: config.groupsApiHost,
        withCredentials: true,
    });
};

export const getLeaguesSearchProxyAxiosInstance = () => {
    return axios.create({
        baseURL: '/search-api',
    });
};

export const getLeaguesSearchApiAxiosInstance = (jwt = null) => {
    if (config.alwaysUseProxy) {
        return getLeaguesSearchProxyAxiosInstance();
    }
    const resolvedJwt = jwt ?? Cookies.get('jwt');
    const instance = axios.create({
        baseURL: config.leaguesSearchApiHost || config.searchApiHost,
        withCredentials: true,
    });
    if (resolvedJwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${resolvedJwt}`;
    }
    addJwtInterceptor(instance);
    return instance;
};

export const getRankingsApiAxiosInstance = (jwt = null) => {
    if (config.alwaysUseProxy) {
        return getRankingsProxyAxiosInstance();
    }
    const instance = axios.create({
        baseURL: config.rankingsApiHost,
        withCredentials: true,
    });
    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }

    addJwtInterceptor(instance);
    return instance;
};

export const getUtrStatsProxyAxiosInstance = () => {
    const instance = axios.create({
        baseURL: '/utr-stats-api',
    });
    addJwtInterceptor(instance);
    return instance;
};

export const getUtrStatsApiAxiosInstance = () => {
    if (config.alwaysUseProxy) {
        return getUtrStatsProxyAxiosInstance();
    }
    const instance = axios.create({
        baseURL: config.utrStatsApiHost,
        withCredentials: true,
    });
    addJwtInterceptor(instance);
    return instance;
};

export const getMessagingApiAxiosInstance = (jwt = Cookies.get('jwt')) => {
    const instance = axios.create({
        baseURL: config.messagingApiHost,
        withCredentials: true,
        headers,
    });

    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }

    addJwtInterceptor(instance);

    return instance;
};

export const getRostersApiAxiosInstance = (jwt = Cookies.get('jwt')) => {
    if (config.alwaysUseProxy) {
        return getRostersProxyAxiosInstance();
    }
    const instance = axios.create({
        baseURL: config.rostersApiHost,
        withCredentials: true,
        headers,
    });
    if (jwt) {
        instance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }

    addJwtInterceptor(instance);
    return instance;
};

export const axiosMock = () => {
    const axiosInstance = axios.create({});
    const mock = new MockAdapter(axiosInstance);

    const apiInstances = {
        mainApi: axiosInstance,
    };

    return {
        mock,
        apiInstances,
    };
};
